import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Empty, Input, Mentions, Modal, Space, Spin, Tag, Typography, message } from 'antd';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { nativeGuestApi, type NativeEditorBootstrap, type NativeGuestSession } from '../services/nativeEditorApi';
import { createNativeId, type NativeAnchor, type NativeCommentThread, type NativeSuggestion } from '../services/nativeEditorModel';

const { Title, Text } = Typography;

const errorMessage = (error: unknown, fallback: string) => {
  if (!axios.isAxiosError(error)) return fallback;
  const detail = error.response?.data?.detail;
  return typeof detail === 'string' ? detail : detail?.message || fallback;
};

const safeReviewHtml = (html: string) => {
  const parsed = new DOMParser().parseFromString(html || '<p></p>', 'text/html');
  parsed.querySelectorAll('script, iframe, object, embed, link, meta').forEach((node) => node.remove());
  parsed.querySelectorAll<HTMLElement>('*').forEach((node) => {
    Array.from(node.attributes).forEach((attribute) => {
      if (attribute.name.toLowerCase().startsWith('on') || /javascript:/i.test(attribute.value)) {
        node.removeAttribute(attribute.name);
      }
    });
  });
  return parsed.body.innerHTML;
};

const captureReviewAnchor = (container: HTMLElement | null): NativeAnchor | null => {
  const selection = window.getSelection();
  if (!container || !selection || selection.rangeCount === 0) return null;
  const range = selection.getRangeAt(0);
  if (!container.contains(range.commonAncestorContainer)) return null;
  const selectedText = range.toString();
  const element = (range.commonAncestorContainer.nodeType === Node.ELEMENT_NODE
    ? range.commonAncestorContainer as HTMLElement
    : range.commonAncestorContainer.parentElement)?.closest<HTMLElement>('[data-native-node-id]');
  if (!element) return { selectedText };
  const offset = (boundaryNode: Node, boundaryOffset: number) => {
    const prefix = document.createRange();
    prefix.selectNodeContents(element);
    prefix.setEnd(boundaryNode, boundaryOffset);
    return prefix.toString().length;
  };
  return {
    nodeId: element.dataset.nativeNodeId,
    start: offset(range.startContainer, range.startOffset),
    end: offset(range.endContainer, range.endOffset),
    selectedText,
    contextBefore: (element.textContent || '').slice(Math.max(0, offset(range.startContainer, range.startOffset) - 24), offset(range.startContainer, range.startOffset)),
    contextAfter: (element.textContent || '').slice(offset(range.endContainer, range.endOffset), offset(range.endContainer, range.endOffset) + 24),
  };
};

const SowNativeGuestReview: React.FC = () => {
  const [searchParams] = useSearchParams();
  const invitationToken = searchParams.get('invite') || '';
  const mentionToken = searchParams.get('mention') || '';
  const accessToken = invitationToken || mentionToken;
  const storageKey = `sow-native-guest:${accessToken.slice(-16)}`;
  const documentRef = useRef<HTMLDivElement | null>(null);
  const [code, setCode] = useState('');
  const [session, setSession] = useState<NativeGuestSession | null>(() => {
    try { return JSON.parse(sessionStorage.getItem(storageKey) || 'null'); } catch { return null; }
  });
  const [workspace, setWorkspace] = useState<NativeEditorBootstrap | null>(null);
  const [threads, setThreads] = useState<NativeCommentThread[]>([]);
  const [suggestions, setSuggestions] = useState<NativeSuggestion[]>([]);
  const [loading, setLoading] = useState(Boolean(session || mentionToken));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [commentOpen, setCommentOpen] = useState(false);
  const [suggestionOpen, setSuggestionOpen] = useState(false);
  const [anchor, setAnchor] = useState<NativeAnchor>({});
  const [body, setBody] = useState('');
  const [replacement, setReplacement] = useState('');

  const load = async (activeSession: NativeGuestSession) => {
    setLoading(true);
    try {
      const data = await nativeGuestApi.bootstrap(activeSession.sowId, activeSession.sessionToken);
      setWorkspace(data);
      setThreads(data.threads || []);
      setSuggestions(data.suggestions || []);
      setError(null);
    } catch (requestError) {
      setError(errorMessage(requestError, 'This review session is unavailable or expired.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (session) {
      void load(session);
    } else if (mentionToken) {
      setLoading(true);
      nativeGuestApi.exchangeMentionLink(mentionToken)
        .then((verified) => {
          sessionStorage.setItem(storageKey, JSON.stringify(verified));
          setSession(verified);
          return load(verified);
        })
        .catch((requestError) => setError(errorMessage(requestError, 'This mention link is invalid or expired.')))
        .finally(() => setLoading(false));
    }
  }, []); // Restore or exchange one secure access link once.

  const verify = async () => {
    if (!invitationToken || code.length !== 6) return;
    setSubmitting(true);
    try {
      const verified = await nativeGuestApi.verify(invitationToken, code);
      sessionStorage.setItem(storageKey, JSON.stringify(verified));
      setSession(verified);
      await load(verified);
    } catch (requestError) {
      setError(errorMessage(requestError, 'The invitation or code is invalid or expired.'));
    } finally {
      setSubmitting(false);
    }
  };

  const mentions = (value: string) => (workspace?.participants || [])
    .filter(({ email }) => value.toLowerCase().includes(`@${email.toLowerCase()}`))
    .map(({ email }) => email);

  const begin = (kind: 'comment' | 'suggestion') => {
    const nextAnchor = captureReviewAnchor(documentRef.current) || {};
    if (kind === 'suggestion' && !nextAnchor.selectedText) {
      message.info('Select the text you want to suggest replacing.');
      return;
    }
    setAnchor(nextAnchor);
    setBody('');
    setReplacement(nextAnchor.selectedText || '');
    kind === 'comment' ? setCommentOpen(true) : setSuggestionOpen(true);
  };

  const addComment = () => {
    if (!session || !body.trim()) return;
    const now = new Date().toISOString();
    setThreads((current) => [...current, {
      id: createNativeId('thread'),
      anchor,
      resolved: false,
      createdAt: now,
      comments: [{
        id: createNativeId('comment'), authorName: session.name, authorEmail: session.email,
        body: body.trim(), mentions: mentions(body), createdAt: now,
      }],
    }]);
    setCommentOpen(false);
  };

  const addSuggestion = () => {
    if (!session || !anchor.selectedText || !replacement.trim()) return;
    setSuggestions((current) => [...current, {
      id: createNativeId('suggestion'), authorName: session.name, authorEmail: session.email,
      originalText: anchor.selectedText || '', replacementText: replacement,
      anchor, status: 'pending', createdAt: new Date().toISOString(),
    }]);
    setSuggestionOpen(false);
  };

  const save = async () => {
    if (!session || !workspace) return;
    setSubmitting(true);
    try {
      const data = await nativeGuestApi.saveReview(session.sowId, session.sessionToken, {
        threads, suggestions, expected_revision: workspace.revision,
      });
      setWorkspace(data);
      setThreads(data.threads || []);
      setSuggestions(data.suggestions || []);
      message.success('Review saved');
    } catch (requestError) {
      setError(errorMessage(requestError, 'The review could not be saved. Reload before trying again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const renderedHtml = useMemo(() => safeReviewHtml(workspace?.html || ''), [workspace?.html]);
  if (!accessToken) return <div className="sow-native-guest-page"><Alert type="error" showIcon message="Secure review link is missing." /></div>;
  if (!session && mentionToken && loading) return <div className="sow-native-guest-page"><Spin size="large" /></div>;
  if (!session && mentionToken && error) return <div className="sow-native-guest-page"><Alert type="error" showIcon message={error} /></div>;
  if (!session) return (
    <div className="sow-native-guest-page">
      <Card className="sow-native-guest-login">
        <Title level={2}>Review SOW</Title>
        <Text>Enter the six-digit code from your invitation email. It is valid for 10 minutes.</Text>
        {error && <Alert type="error" showIcon message={error} />}
        <Input.OTP length={6} value={code} onChange={setCode} />
        <Button type="primary" block loading={submitting} disabled={code.length !== 6} onClick={() => void verify()}>Open secure review</Button>
      </Card>
    </div>
  );
  if (loading) return <div className="sow-native-guest-page"><Spin size="large" /></div>;
  if (!workspace) return <div className="sow-native-guest-page"><Alert type="error" showIcon message={error || 'Review unavailable'} /></div>;

  const openThreads = threads.filter(({ resolved }) => !resolved);
  const pendingSuggestions = suggestions.filter(({ status }) => status === 'pending');
  return (
    <div className="sow-native-guest-page">
      <div className="sow-native-guest-header">
        <div><Title level={3}>SOW review</Title><Text>{session.name} · comment and suggest only</Text></div>
        <Space><Tag>Draft v{workspace.sourceVersion}</Tag><Button onClick={() => begin('comment')}>Comment</Button><Button onClick={() => begin('suggestion')}>Suggest change</Button><Button type="primary" loading={submitting} onClick={() => void save()}>Save review</Button></Space>
      </div>
      {error && <Alert closable onClose={() => setError(null)} type="error" showIcon message={error} />}
      <div className="sow-native-guest-layout">
        <div ref={documentRef} className="sow-native-editable sow-native-guest-document" dangerouslySetInnerHTML={{ __html: renderedHtml }} />
        <aside className="sow-native-review-column">
          <Title level={5}>Open comments <Tag>{openThreads.length}</Tag></Title>
          {openThreads.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> : openThreads.map((thread) => <Card key={thread.id} size="small" className="sow-native-review-card"><Text mark>{thread.anchor.selectedText || 'Document'}</Text>{thread.comments.map((comment) => <p key={comment.id}><Text strong>{comment.authorName}</Text><br />{comment.body}</p>)}</Card>)}
          <Title level={5}>Suggestions <Tag>{pendingSuggestions.length}</Tag></Title>
          {pendingSuggestions.map((suggestion) => <Card key={suggestion.id} size="small" className="sow-native-review-card"><Text delete>{suggestion.originalText}</Text><br /><Text className="sow-native-inserted-text">{suggestion.replacementText}</Text></Card>)}
        </aside>
      </div>
      <Modal title="Add comment" open={commentOpen} okButtonProps={{ disabled: !body.trim() }} onOk={addComment} onCancel={() => setCommentOpen(false)}><Mentions autoSize={{ minRows: 4 }} value={body} onChange={setBody} options={(workspace.participants || []).map((participant) => ({ value: participant.email, label: `${participant.name} (${participant.email})` }))} /></Modal>
      <Modal title="Suggest replacement" open={suggestionOpen} okButtonProps={{ disabled: !replacement.trim() }} onOk={addSuggestion} onCancel={() => setSuggestionOpen(false)}><Text type="secondary">Original</Text><Input.TextArea disabled value={anchor.selectedText} /><Text type="secondary">Suggested replacement</Text><Input.TextArea value={replacement} onChange={(event) => setReplacement(event.target.value)} /></Modal>
    </div>
  );
};

export default SowNativeGuestReview;
